import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Row } from 'react-table';
import { toast } from 'react-toastify';

import { ActionButton } from 'app/components/Button/ActionButton';
import ModalDefault from 'app/components/Modal/ModalDefault';
import TableComponent from 'app/components/Table/TableComponent';
import { useListView } from 'app/components/Table/core/ListViewProvider';
import { useQueryResponse } from 'app/components/Table/core/QueryResponseProvider';
import { emptyListViewProvider } from 'app/components/Table/core/constants';
import { LoadingCard } from 'app/components/Table/loading/LoadingCard';

import { SensorSkuId } from 'app/modules/inventory/bins/core/enum';
import { getSensors } from 'app/modules/system-settings/devices/core/requests';

import { getResponseError } from 'app/helpers/errors';
import { useBoolean } from 'app/hooks';
import isUndefined from 'lodash/isUndefined';

import { Item } from '../../items/core/type';
import { sensorColumns } from '../core/columns';
import { updateBin } from '../core/requests';
import { Bin } from '../core/type';
import { useOrganizationStore } from 'theme/layout/components/aside/store';

const MIN_NONCHARGEABLE_INDEX = 5;
const MAX_NONCHARGEABLE_INDEX = 29;

export const getStockType = (index: number) => {
  return index <= MAX_NONCHARGEABLE_INDEX && index >= MIN_NONCHARGEABLE_INDEX ? 2 : 1;
};

interface Props {
  onClickCloseModal?: (updateBin?: Bin) => void;
  onUpdateRack?: () => void;
  selectBin: Bin | null;
  showUnableToAssignModal?: () => void;
}

const AssignSensorModal: React.FC<Props> = ({
  selectBin,
  onUpdateRack,
  onClickCloseModal,
  showUnableToAssignModal,
}) => {
  const { refetch } = useQueryResponse();
  const { setItemForUpdate } = useListView();
  const [selectItem, setSelectItem] = useState<Item | null>(null);
  const [refetchTimestamp, setRefetchTimestamp] = useState<number>(new Date().getTime());
  const isLoading = useBoolean();
  const isLoadingSubmit = useBoolean();
  const organizationId = useOrganizationStore((state) => state.state.organization?.id);

  const { data: unassignedSensors } = useQuery({
    queryKey: ['sensors', 'unassigned', organizationId],
    queryFn: () => getSensors(`page=1&limit=999&orgId=${organizationId}&type=unassigned`),
    select: (data) => {
      const filteredSensors =
        data?.data?.items?.filter((sensor) => sensor.skuId !== SensorSkuId.Expiry) ?? [];

      return {
        items: filteredSensors,
        total: filteredSensors.length,
      };
    },
  });

  useEffect(() => {
    setRefetchTimestamp(new Date().getTime());
  }, [unassignedSensors]);

  const onAssignItem = async () => {
    if (!selectBin?.id || !selectItem?.id) {
      return;
    }

    if (selectBin?.item?.qty && selectBin?.item?.qty > 0) {
      cancel();
      showUnableToAssignModal?.();
      return;
    }

    isLoadingSubmit.setValue(true);

    try {
      await updateBin(selectBin?.id, {
        itemId: Number(selectItem?.id),
      });
      toast.success(`Assign sensor successfully!`);
      if (!isUndefined(onClickCloseModal)) {
        onClickCloseModal({
          ...selectBin,
          item: selectItem,
        });
        return;
      }
      if (onUpdateRack) {
        onUpdateRack();
      }
      cancel(true);
    } catch (error: any) {
      getResponseError(error);
    } finally {
      isLoadingSubmit.setValue(false);
    }
  };
  const cancel = (withRefresh?: boolean) => {
    if (!isUndefined(onClickCloseModal)) {
      onClickCloseModal();
      return;
    }
    if (withRefresh) {
      refetch();
    }
    setItemForUpdate(emptyListViewProvider);
  };

  const onClickBodyTable = (row: Row) => () => {
    const newItem = row.original as Item;
    setSelectItem(newItem);
  };

  const getListSensor = () =>
    Promise.resolve({
      data: {
        items: unassignedSensors?.items ?? [],
        total: unassignedSensors?.total ?? 0,
      },
    });

  return (
    <ModalDefault
      title="Assign sensor"
      onClickCloseModal={() => cancel()}
      childrenAction={
        <ActionButton
          onCancel={() => cancel()}
          disableButton={isLoadingSubmit.value || !selectItem}
          isSubmitting={isLoadingSubmit.value}
          labelButtonSubmit="Assign"
          onSubmit={onAssignItem}
        />
      }
      modalContentStyle={{ height: 'calc(100vh - 112px)' }}
      styleBodyModalDialog={{
        padding: 0,
        flexGrow: 1,
        height: 0,
        maxHeight: 'none',
        overflow: 'hidden',
      }}
    >
      <TableComponent
        originColumns={useMemo(() => sensorColumns(), [])}
        moreQuery={`&status=1&orgId=${organizationId}&type=unassigned`}
        nameQuery={''}
        getList={getListSensor}
        isBorder={false}
        onClickBodyTable={onClickBodyTable}
        placeholderSearch="Search with sensor name"
        bodyStyle={{ flexGrow: 1, height: 0, overflow: 'auto' }}
        isLoadMore={true}
        isSortAndSearchFrontEnd={true}
        tableContainerStyle={{ flexGrow: 1, height: 0 }}
        tableStyle={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}
        spacing={'sm'}
        cardStyle={{ height: '100%' }}
        choseRowId={Number(selectItem?.id)}
        tableContentStyle={{ height: '100%' }}
        defaultState={{ limit: 999, page: 1 }}
        refetchTimestamp={refetchTimestamp}
      />

      {isLoading.value || isLoadingSubmit.value ? <LoadingCard /> : null}
    </ModalDefault>
  );
};
export default AssignSensorModal;
